import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ArrowLeft, ExternalLink, Globe, Shield, TrendingUp } from "lucide-react";

import { TREND_REPORT_TYPE } from "../api/trends";
import { api } from "../utils/apiClient";
import { isLoggedIn } from "../utils/jwtUtils";
import { LLM_COLORS } from "../utils/llmColors";
import CrossReportBanner from "./CrossReportBanner";
import DownloadPdfButton from "./DownloadPdfButton";
import LoadingSpinner from "./LoadingSpinner";

const LinkRow = ({ link }) => {
  const isDirect = link.classification === "direct";
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-container-lowest hover:bg-surface-container-high transition-colors group">
      <span className="text-on-surface-variant/50 text-xs font-mono w-5 shrink-0 text-right">
        {link.position}.
      </span>
      <a
        href={link.url.startsWith("http") ? link.url : `https://${link.url}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs font-mono text-on-surface truncate flex-1 group-hover:text-primary transition-colors"
        title={link.url}
      >
        {link.url}
      </a>
      <ExternalLink className="w-3 h-3 text-outline opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
      <span
        className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${
          isDirect ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
        }`}
      >
        {isDirect ? "Direct" : "OTA"}
      </span>
    </div>
  );
};

const ProviderCard = ({ result }) => {
  const style = LLM_COLORS[result.provider] || {
    label: result.provider,
    text: "text-on-surface",
    bg: "bg-surface-container",
    border: "border-outline/10",
  };
  const total = result.links.length;
  const directPct = total > 0 ? Math.round((result.direct_count / total) * 100) : 0;

  return (
    <div className={`rounded-2xl border ${style.border} ${style.bg} p-6`}>
      <div className="flex items-center justify-between mb-4">
        <span className={`text-sm font-bold uppercase tracking-widest ${style.text}`}>
          {style.label}
        </span>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-emerald-400 font-bold">{result.direct_count} Direct</span>
          <span className="text-on-surface-variant/40">·</span>
          <span className="text-red-400 font-bold">{result.ota_count} OTA</span>
        </div>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="mb-4">
          <div className="flex h-2 rounded-full overflow-hidden bg-surface-container-low">
            <div
              className="bg-emerald-500 transition-all duration-500"
              style={{ width: `${directPct}%` }}
            />
            <div className="bg-red-500/60 flex-1" />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-emerald-400">{directPct}% direct</span>
            <span className="text-[10px] text-red-400">{100 - directPct}% OTA</span>
          </div>
        </div>
      )}

      {result.links.length === 0 ? (
        <p className="text-xs text-on-surface-variant/60 text-center py-4">
          No citation links returned by this provider
        </p>
      ) : (
        <div className="space-y-1.5">
          {result.links.map((link) => (
            <LinkRow key={link.position} link={link} />
          ))}
        </div>
      )}
    </div>
  );
};

const OTAReport = () => {
  const { ota_report_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [report, setReport] = useState(location.state?.otaResponse || null);
  const [loading, setLoading] = useState(!report);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (report) return;
    const load = async () => {
      try {
        const res = await api.get(`/ota-report/${ota_report_id}`);
        setReport(res.data);
      } catch {
        setError("Report not found or you don't have access.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ota_report_id, report]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading your OTA report…" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-on-surface-variant mb-4">{error || "Report not found."}</p>
          <button
            onClick={() => navigate("/ota-direct")}
            className="text-primary hover:underline text-sm font-medium"
          >
            Run a new report
          </button>
        </div>
      </div>
    );
  }

  const totalLinks = report.overall_direct_count + report.overall_ota_count;
  const overallDirectPct =
    totalLinks > 0 ? Math.round((report.overall_direct_count / totalLinks) * 100) : 0;

  const scoreColor =
    overallDirectPct >= 60
      ? "text-emerald-400"
      : overallDirectPct >= 30
        ? "text-yellow-400"
        : "text-red-400";

  return (
    <main className="pt-10 pb-24 px-6 md:px-12 max-w-8xl mx-auto">
      {/* Back */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate("/ota-direct")}
          className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          New report
        </button>
        {isLoggedIn() && (
          <button
            onClick={() => navigate(`/trends?type=${TREND_REPORT_TYPE.OTA_VS_DIRECT}`)}
            title="View Trends"
            aria-label="View Trends"
            className="p-2 text-on-surface bg-surface-container-low border border-surface-container-high hover:bg-surface-container-high rounded-xl transition-colors flex items-center justify-center"
          >
            <TrendingUp className="w-4 h-4" />
          </button>
        )}
        <DownloadPdfButton reportType="ota_vs_direct" reportId={ota_report_id} />
      </div>

      {/* Header */}
      <div className="mb-10">
        <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-label text-[10px] font-extrabold tracking-[0.15em] mb-4 uppercase">
          OTA vs Direct Report
        </span>
        <h1 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tight text-on-surface mb-3">
          {report.business_name}
        </h1>
        <p className="text-on-surface-variant text-sm">
          Query:{" "}
          <span className="font-mono text-on-surface bg-surface-container-low px-2 py-0.5 rounded-lg">
            &ldquo;{report.query}&rdquo;
          </span>
        </p>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <div className="bg-surface-container-low rounded-2xl p-6 border border-outline/10 text-center">
          <div className={`text-4xl font-black mb-1 ${scoreColor}`}>{overallDirectPct}%</div>
          <div className="text-xs text-on-surface-variant font-medium uppercase tracking-widest">
            Direct links overall
          </div>
        </div>
        <div className="bg-emerald-500/10 rounded-2xl p-6 border border-emerald-500/20 text-center">
          <div className="text-4xl font-black text-emerald-400 mb-1">
            {report.overall_direct_count}
          </div>
          <div className="flex items-center justify-center gap-1.5 text-xs text-emerald-400/80 font-medium uppercase tracking-widest">
            <Shield className="w-3 h-3" />
            Direct links
          </div>
        </div>
        <div className="bg-red-500/10 rounded-2xl p-6 border border-red-500/20 text-center">
          <div className="text-4xl font-black text-red-400 mb-1">{report.overall_ota_count}</div>
          <div className="flex items-center justify-center gap-1.5 text-xs text-red-400/80 font-medium uppercase tracking-widest">
            <Globe className="w-3 h-3" />
            OTA links
          </div>
        </div>
      </div>

      {/* Score interpretation */}
      <div className="mb-12 p-5 rounded-2xl bg-surface-container-low border border-outline/10">
        <p className="text-sm text-on-surface-variant leading-relaxed">
          {overallDirectPct >= 60 ? (
            <>
              <span className="text-emerald-400 font-bold">Strong direct presence.</span> Most AI
              citations send travellers directly to your website, preserving your margin. Keep up
              your direct booking SEO and structured data.
            </>
          ) : overallDirectPct >= 30 ? (
            <>
              <span className="text-yellow-400 font-bold">Mixed results.</span> AI is splitting
              traffic between your site and OTAs. Improving your direct booking visibility — schema
              markup, Google Business, and direct-booking deals — can shift this balance.
            </>
          ) : (
            <>
              <span className="text-red-400 font-bold">High OTA dominance.</span> AI is primarily
              directing guests to third-party platforms. OTA commissions (15–25%) are reducing your
              revenue on these bookings. Consider strengthening your direct booking presence.
            </>
          )}
        </p>
      </div>

      {/* Per-provider results */}
      <h2 className="font-headline text-2xl font-bold text-on-surface mb-6">Results by Provider</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {report.provider_results.map((result) => (
          <ProviderCard key={result.provider} result={result} />
        ))}
      </div>

      {report.provider_results.length === 0 && (
        <div className="text-center py-16 text-on-surface-variant">
          <Globe className="w-12 h-12 mx-auto mb-4 text-outline" />
          <p>No citation links were returned by any provider for this query.</p>
          <button
            onClick={() => navigate("/ota-direct")}
            className="mt-4 text-primary hover:underline text-sm font-medium"
          >
            Try a different query
          </button>
        </div>
      )}

      {/* Domain note */}
      <p className="mt-10 text-xs text-on-surface-variant/50 text-center">
        Links classified as <span className="text-emerald-400 font-semibold">Direct</span> point to{" "}
        <span className="font-mono">{report.business_domain}</span> or its subdomains / same-brand
        TLDs. <span className="text-red-400 font-semibold">OTA</span> links go to third-party
        platforms.
      </p>

      <CrossReportBanner currentReport="ota" />
    </main>
  );
};

export default OTAReport;
